import React, { useState, useContext } from "react";
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useNavigate } from "react-router-dom";
import {
    Box, TextField, Button, Typography, Alert, Divider,
    IconButton, InputAdornment
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import GoogleIcon from "@mui/icons-material/Google";
import firebase from "firebase/compat/app";
import { FirebaseAuth } from "../../../components/FirebaseAuth/firebase";
import { AuthContext } from "../../../components/FirebaseAuth";
import { userSignIn } from "../../../libs/user";
import AuthLayout from "../../../components/AuthLayout";
import Loader from "../../../components/Loader";

const SignIn = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { authUser } = useContext(AuthContext);

    const params = new URL(document.location).searchParams;
    const re = params.get('re');
    if (re && re.indexOf('/') === 0) {
        localStorage.setItem('re', re);
    }

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    // Redirect if already signed in (but not if we're in the middle of signing in).
    // Rendered declaratively (not an imperative navigate() call during render,
    // which trips React's "Cannot update a component while rendering a
    // different component" warning).
    if (!loading && authUser.checked && authUser.user) {
        return <Navigate to="/" replace />;
    }

    const redirectAfterSignIn = () => {
        const to = localStorage.getItem('re');
        localStorage.removeItem('re');
        setLoading(false);
        if (to && to.indexOf('/') === 0) {
            navigate(to, { replace: true });
        } else {
            navigate('/', { replace: true });
        }
    };

    const handleEmailSignIn = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            await FirebaseAuth.auth().signInWithEmailAndPassword(email, password);
            userSignIn((success) => {
                if (success) {
                    redirectAfterSignIn();
                } else {
                    setError(t('auth.signInSyncFailed'));
                    setLoading(false);
                }
            });
        } catch (err) {
            setError(err.message);
            setLoading(false);
        }
    };

    const handleGoogleSignIn = async () => {
        setError("");
        setLoading(true);
        try {
            const provider = new firebase.auth.GoogleAuthProvider();
            await FirebaseAuth.auth().signInWithPopup(provider);
            userSignIn((success) => {
                if (success) {
                    redirectAfterSignIn();
                } else {
                    setError(t('auth.googleSignInSyncFailed'));
                    setLoading(false);
                }
            });
        } catch (err) {
            setError(err.message);
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <AuthLayout>
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Loader text={t('auth.signingIn')} />
                </Box>
            </AuthLayout>
        );
    }

    return (
        <AuthLayout>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, textAlign: 'center' }}>
                {t('auth.signInTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
                {t('auth.signInSubtitle')}
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Box component="form" id="sign-in-form" name="sign-in" onSubmit={handleEmailSignIn} noValidate>
                <TextField
                    label={t('auth.workEmail')}
                    type="email"
                    fullWidth
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    sx={{ mb: 2 }}
                    autoComplete="email"
                />
                <TextField
                    label={t('auth.password')}
                    type={showPassword ? "text" : "password"}
                    fullWidth
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    sx={{ mb: 1 }}
                    autoComplete="current-password"
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
                <Typography variant="body2" sx={{ textAlign: 'right', mb: 2 }}>
                    <Link to="/forgot-password" style={{ textDecoration: 'none' }}>{t('auth.forgotPassword')}</Link>
                </Typography>
                <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    size="large"
                    sx={{ mb: 2, textTransform: 'none', fontWeight: 600 }}
                >
                    {t('auth.signIn')}
                </Button>
            </Box>

            <Divider sx={{ my: 2 }}>{t('auth.or')}</Divider>

            <Button
                variant="outlined"
                fullWidth
                size="large"
                startIcon={<GoogleIcon />}
                onClick={handleGoogleSignIn}
                sx={{ mb: 3, textTransform: 'none', fontWeight: 600 }}
            >
                {t('auth.signInWithGoogle')}
            </Button>

            <Typography variant="body2" sx={{ textAlign: 'center' }}>
                {t('auth.noAccount')}{" "}
                <Link to="/signup" style={{ textDecoration: 'none' }}>{t('auth.signUp')}</Link>
            </Typography>
        </AuthLayout>
    );
};

export default SignIn;
